import { EstateForSalePageFragment } from '@gql'
import { InfoIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateSettingOpeningFormPrices } from './EstateSettingOpeningFormPrices'
import { EstateSettingRoundDates } from './EstateSettingRoundDates'
import { EstateSettingRoundOptions } from './EstateSettingRoundOptions'

type EstateSettingOpeningTabProps = {
  estate: Pick<
    EstateForSalePageFragment,
    'id' | 'firstPrice' | 'reservePrice' | 'estateRounds' | 'currentRoundId'
  >
  /** Refers only to the permission system (not core logic) */
  readOnly?: boolean
}

export const EstateSettingOpeningTab = ({
  estate,
  readOnly,
}: EstateSettingOpeningTabProps) => {
  const { t } = useTranslation()

  const currentRound = useMemo(() => {
    return estate.estateRounds.find(
      (round) => round.id === estate.currentRoundId
    )
  }, [estate.estateRounds, estate.currentRoundId])

  const firstRound = useMemo(() => {
    return estate.estateRounds.find((round) => round.number === 1)
  }, [estate.estateRounds])

  const isCurrentRoundFirstRound = useMemo(() => {
    return currentRound?.number === 1
  }, [currentRound])

  const hasFirstRoundBegun = useMemo(() => {
    return firstRound != null && new Date(firstRound.startDate) < new Date()
  }, [firstRound])

  const hasFirstRoundEnded = useMemo(() => {
    return firstRound != null && new Date(firstRound.endDate) < new Date()
  }, [firstRound])

  const canEditStartDate = useMemo(() => {
    return !readOnly && isCurrentRoundFirstRound && !hasFirstRoundBegun
  }, [readOnly, hasFirstRoundBegun, isCurrentRoundFirstRound])

  const canEditEndDate = useMemo(() => {
    return !readOnly && isCurrentRoundFirstRound && !hasFirstRoundEnded
  }, [readOnly, isCurrentRoundFirstRound, hasFirstRoundEnded])

  return (
    <div className="tw-space-y-6 tw-h-full">
      {firstRound && (
        <>
          <EstateSettingOpeningFormPrices estate={estate} readOnly={readOnly} />
          {!readOnly && (
            <div className="tw-py-3 tw-p-4 tw-bg-green-200 tw-rounded-lg">
              <p className="tw-text-sm tw-font-normal tw-text-green-700 tw-flex tw-items-center">
                <InfoIcon strokeWidth={3} className="tw-mr-3" />
                {t('EstateSettingOpeningForm.description')}
              </p>
            </div>
          )}

          <h2 className="tw-text-lg tw-font-semibold">
            {t('EstateSettingOpeningForm.firstRoundSettings.title')}
          </h2>

          {hasFirstRoundBegun && (
            <div className="tw-py-3 tw-p-4 tw-bg-green-200 tw-rounded-lg">
              <p className="tw-text-sm tw-font-normal tw-text-green-700 tw-flex tw-items-center">
                <InfoIcon strokeWidth={3} className="tw-mr-3" />
                {t('EstateSettingOpeningForm.firstRoundSettings.info')}
              </p>
            </div>
          )}

          <EstateSettingRoundOptions
            round={firstRound}
            readOnly={readOnly || !isCurrentRoundFirstRound}
          />

          <EstateSettingRoundDates
            round={firstRound}
            readOnly={readOnly}
            canEditStartDate={canEditStartDate}
            canEditEndDate={canEditEndDate}
          />
        </>
      )}
    </div>
  )
}
